import * as XLSX from 'xlsx';

/**
 * 分页循环加载全部数据
 * @param {Function} loadDataFn 加载数据的函数，接收 (pageNo, pageSize) 参数，返回 Promise
 * @param {Object} options 配置选项
 * @param {number} options.pageSize 每页大小，默认1000
 * @param {Function} options.onProgress 进度回调函数，接收 (current, total) 参数
 * @returns {Promise<Array>} 返回所有数据的数组
 */
export async function loadAllDataByPagination(loadDataFn, options = {}) {
  const { pageSize = 1000, onProgress } = options;
  let allData = [];
  let pageNo = 0;
  let totalCount = 0;
  let hasMore = true;

  while (hasMore) {
    try {
      const result = await loadDataFn(pageNo, pageSize);

      if (!result || !result.data) {
        break;
      }

      const { data, total } = result;

      // 第一次获取总数
      if (pageNo === 0) {
        totalCount = total || 0;
      }

      allData = allData.concat(data);
      pageNo++;

      // 判断是否还有更多数据
      hasMore = data.length === pageSize && allData.length < totalCount;

      // 调用进度回调
      if (onProgress && typeof onProgress === 'function') {
        onProgress(allData.length, totalCount);
      }

      // 添加小延迟避免请求过于频繁
      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error('分页加载数据失败:', error);
      throw error;
    }
  }

  return allData;
}

/**
 * 将数据导出为Excel文件
 * @param {Array} data 要导出的数据数组
 * @param {Array} columns 列配置数组，每个元素包含 { key, title, formatter? }
 * @param {string} filename 文件名（不含扩展名）
 * @param {Object} options 其他配置选项
 */
export function exportToExcel(data, columns, filename, options = {}) {
  try {
    // 处理列标题
    const headers = columns.map(col => col.title || col.key);

    // 处理数据行
    const rows = data.map(item => {
      return columns.map(col => {
        let value = getNestedValue(item, col.key);

        // 如果有格式化函数，使用格式化函数
        if (col.formatter && typeof col.formatter === 'function') {
          value = col.formatter(value, item);
        }

        return value;
      });
    });

    // 创建工作表数据
    const wsData = [headers, ...rows];

    // 创建工作表
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 设置列宽
    const colWidths = columns.map(col => ({ wch: col.width || 15 }));
    ws['!cols'] = colWidths;

    // 创建工作簿
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Sheet1');

    // 生成文件
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    // 保存文件
    // const blob = new Blob([wbout], { type: 'application/octet-stream' });
    // const finalFilename = `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
    // saveAs(blob, finalFilename);

     // 创建Blob并生成URL
     const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
     const url = URL.createObjectURL(blob);
     // 返回URL而不是直接保存文件
     return url;
  } catch (error) {
    console.error('导出Excel失败:', error);
    throw error;
  }
}

/**
 * 获取对象嵌套属性值
 * @param {Object} obj 对象
 * @param {string} path 属性路径，支持 'a.b.c' 格式
 * @returns {any} 属性值
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return '';

  const keys = path.split('.');
  let result = obj;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return '';
    }
    result = result[key];
  }

  return result === null || result === undefined ? '' : result;
}

/**
 * 下载URL
 * @param {*} downloadUrl downloadUrl 下载链接
 * @param {*} filename filename 文件名
 */
export function downloadUrl(downloadUrl, filename="filename.xlsx") {
  // 可以创建一个下载链接
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  link.click();

  // 记得在不需要时释放URL
  // URL.revokeObjectURL(downloadUrl);
}